<template>
  <v-dialog
    v-model="visible"
    persistent
    width="1200"
    scrollable
  >
    <v-card :loading="loading">
      <v-card-title> New Cases </v-card-title>
      <v-card-text>
        <v-tabs>
          <v-tab href="#newCase">New Case</v-tab>
          <v-tab-item value="newCase">
            <v-card flat>
              <v-card-text>
                <v-form ref="newCaseForm">
                  <v-row>
                    <!-- Local ID -->
                    <v-col cols="2">
                      <v-text-field
                        dense
                        :maxlength="20"
                        :label="$t('modal.addCase.label.localId')"
                        v-model="newCase.localId"
                        :hint="$t('hint.localId')"
                        persistent-hint
                        :error-messages="uniqueIdErrors"
                        :error="uniqueIdErrors.length > 0"
                      />
                    </v-col>
                    <!-- Type -->
                    <v-col cols="2">
                      <v-select
                        dense
                        :items="getEntityTypes"
                        v-model="newCase.type"
                        :label="$t('label.type')"
                      >
                        <template v-slot:item="{ item }">
                          <span>{{ capitalizeFirstLetter(item) }}</span>
                        </template>
                        <template v-slot:selection="{ item }">
                          <span>{{ capitalizeFirstLetter(item) }}</span>
                        </template>
                      </v-select>
                    </v-col>

                    <v-spacer />
                    <!-- Name -->
                    <v-col cols="7">
                      <v-text-field
                        dense
                        :maxlength="100"
                        :label="$t('label.name')"
                        v-model="newCase.name"
                        :rules="[rules.required]"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <!-- Case Group -->
                    <v-col cols="4">
                      <v-autocomplete
                        v-model="newCase.caseGroupId"
                        :items="caseGroups"
                        label="Case Group"
                        hint="Case group for default assignee, etc."
                        clearable
                        persistent-hint
                        item-text="name"
                        item-value="id"
                        dense
                      />
                    </v-col>
                    <v-col cols="1"></v-col>
                    <v-col>
                      <v-text-field
                        dense
                        :maxlength="100"
                        :label="$t('label.title')"
                        v-model="newCase.title"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="2">
                      <v-text-field
                        dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.phoneNumber')"
                        v-model="newCase.phoneNumber"
                      />
                    </v-col>
                    <v-col cols="2">
                      <v-select
                        dense
                        clearable
                        :items="genders"
                        v-model="newCase.gender"
                        :label="$t('modal.addCase.label.gender')"
                      />
                    </v-col>
                    <v-col cols="1"></v-col>
                    <v-col cols="3">
                      <v-text-field
                        dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.citizenship')"
                        v-model="newCase.citizenship"
                      />
                    </v-col>
                    <v-col cols="3">
                      <v-text-field
                        dense
                        :maxlength="100"
                        label="Nationality"
                        v-model="newCase.nationality"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="4">
                      <v-menu
                        v-model="dobPicker"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            dense
                            clearable
                            v-model="newCase.dob"
                            :label="$t('modal.addCase.label.dob')"
                            append-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          no-title
                          v-model="newCase.dob"
                          :show-current="false"
                          @input="dobPicker = false"
                        >
                        </v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="1"></v-col>
                    <v-col cols="3">
                      <v-text-field
                        dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.placeOfBirth')"
                        v-model="newCase.placeOfBirth"
                      />
                    </v-col>
                  </v-row>
                  <!-- Client custom labels -->
                  <v-row v-if="getAccountSettings.customLabels">
                    <v-col
                      cols="3"
                      v-for="(value, labelName) in getAccountSettings.customLabels"
                      :key="labelName"
                    >
                      <v-select
                        :items="value"
                        :label="labelName"
                        v-model="customLabels[labelName]"
                        clearable
                        dense
                      ></v-select>
                    </v-col>
                  </v-row>

                  <!-- Client custom fields -->
                  <v-row v-if="getAccountSettings.customFields">
                    <v-col
                      cols="3"
                      v-for="fieldName in getAccountSettings.customFields"
                      :key="fieldName"
                    >
                      <v-text-field
                        dense
                        :label="fieldName"
                        v-model="customFields[fieldName]"
                      />
                    </v-col>
                  </v-row>

                  <v-row align="center">
                    <v-col cols="2">
                      <h3>{{ $t("label.address") }}</h3>
                    </v-col>
                    <v-col>
                      <!-- <v-divider /> -->
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="6">
                      <v-text-field
                        dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.address1')"
                        v-model="newCase.address.address1"
                      />
                    </v-col>
                    <v-col cols="2">
                      <v-text-field
                        dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.address2')"
                        v-model="newCase.address.address2"
                      />
                    </v-col>
                    <v-col cols="2">
                      <v-text-field
                        dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.address3')"
                        v-model="newCase.address.address3"
                      />
                    </v-col>
                    <v-col cols="2">
                      <v-text-field
                        dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.address4')"
                        v-model="newCase.address.address4"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="3">
                      <v-text-field
                        dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.city')"
                        v-model="newCase.address.city"
                      />
                    </v-col>
                    <v-col cols="3">
                      <v-text-field
                        dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.stateOrProvince')"
                        v-model="newCase.address.stateOrProvince"
                      />
                    </v-col>
                    <v-col cols="3">
                      <v-text-field
                        dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.postalCode')"
                        v-model="newCase.address.postalCode"
                      />
                    </v-col>
                    <v-col cols="3">
                      <v-text-field
                        dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.country')"
                        v-model="newCase.address.country"
                      />
                    </v-col>
                  </v-row>

                  <!-- IDs -->
                  <v-row>
                    <v-col cols="12">
                      <v-row align="center">
                        <v-col>
                          <h3>IDs</h3>
                        </v-col>
                        <v-col cols="1">
                          <v-btn
                            color="green lighten-1"
                            icon
                            @click="addId"
                          >
                            <v-icon dark>mdi-plus</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>

                      <!-- Click to add IDs banner -->
                      <v-row
                        v-if="!caseIds || caseIds.length == 0"
                        dense
                      >
                        <v-alert
                          dense
                          outlined
                          type="info"
                          >Click
                          <v-icon
                            small
                            color="success"
                            >mdi-plus</v-icon
                          >
                          to add an ID.</v-alert
                        >
                      </v-row>

                      <!-- Current IDs loop -->
                      <v-slide-y-transition
                        class="py-0"
                        group
                      >
                        <template v-for="(caseId, index) in caseIds">
                          <v-sheet
                            :key="index"
                            outlined
                            class="pl-4 my-4 py-4"
                          >
                            <v-row>
                              <v-col cols="3">
                                <v-combobox
                                  label="Type (Select or enter a type)"
                                  class="text-body-1"
                                  dense
                                  :items="getIdTypes"
                                  v-model="caseId.type"
                                />
                              </v-col>
                              <v-col cols="3">
                                <v-text-field
                                  label="Description"
                                  class="text-body-1"
                                  dense
                                  hide-details
                                  :maxlength="100"
                                  v-model="caseId.description"
                                />
                              </v-col>
                              <v-col cols="1">
                                <v-text-field
                                  class="text-body-1"
                                  dense
                                  label="Country"
                                  hide-details
                                  :maxlength="100"
                                  v-model="caseId.country"
                                />
                              </v-col>
                              <v-col cols="4">
                                <v-text-field
                                  label="ID Number"
                                  class="text-body-1"
                                  hide-details
                                  dense
                                  :maxlength="100"
                                  v-model="caseId.idNumber"
                                />
                              </v-col>
                              <v-col cols="1">
                                <v-btn
                                  color="red lighten-1"
                                  dark
                                  icon
                                  @click="deleteId(index)"
                                >
                                  <v-icon dark>mdi-close</v-icon>
                                </v-btn>
                              </v-col>
                              <v-col cols="3">
                                <v-menu
                                  v-model="idIssueDatePicker[index]"
                                  :close-on-content-click="false"
                                  transition="scale-transition"
                                  offset-y
                                  min-width="auto"
                                >
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                      label="Issue Date"
                                      class="text-body-1"
                                      dense
                                      clearable
                                      v-model="caseId.issueDate"
                                      append-icon="mdi-calendar"
                                      readonly
                                      v-bind="attrs"
                                      v-on="on"
                                    ></v-text-field>
                                  </template>
                                  <v-date-picker
                                    v-model="caseId.issueDate"
                                    :show-current="false"
                                    @input="idIssueDatePicker[index] = false"
                                  >
                                  </v-date-picker>
                                </v-menu>
                              </v-col>
                              <v-col cols="3">
                                <v-menu
                                  v-model="idExpirationDatePicker[index]"
                                  :close-on-content-click="false"
                                  transition="scale-transition"
                                  offset-y
                                  min-width="auto"
                                >
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                      label="Expiration Date"
                                      class="text-body-1"
                                      dense
                                      clearable
                                      v-model="caseId.expirationDate"
                                      append-icon="mdi-calendar"
                                      readonly
                                      v-bind="attrs"
                                      v-on="on"
                                    ></v-text-field>
                                  </template>
                                  <v-date-picker
                                    v-model="caseId.expirationDate"
                                    :show-current="false"
                                    @input="idExpirationDatePicker[index] = false"
                                  >
                                  </v-date-picker>
                                </v-menu>
                              </v-col>
                            </v-row>
                          </v-sheet>
                        </template>
                      </v-slide-y-transition>
                    </v-col>
                  </v-row>
                </v-form>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn
                  color="primary"
                  text
                  @click="onCancel"
                  >{{ $t("label.cancel") }}</v-btn
                >
                <v-btn
                  color="primary"
                  @click="onSave"
                  :loading="loading"
                  dark
                  >{{ $t("label.save") }}</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-tab-item>

          <v-tab href="#uploadCase">Import Cases</v-tab>
          <v-tab-item value="uploadCase">
            <CaseCSVForm
              pageTitle="Import Cases"
              v-on:onCancel="onCancel"
              v-on:onConfirmed="onCsvUploadConfirmed"
              :buttonsDisabled="loading"
              :entityTypes="getEntityTypes"
            />
          </v-tab-item>
        </v-tabs>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { saveCaseApi, saveCasesApi } from "../../api/caseApi";
import { mapActions, mapGetters } from "vuex";
import { getMinDateString } from "../../utils/dateUtils";
import { capitalizeFirstLetter } from "../../utils/utils";
import CaseCSVForm from "../../components/CaseCSVForm";
const idStruct = { type: "", country: "", idNumber: "" };
export default {
  name: "AddCase",
  components: { CaseCSVForm },
  props: {
    caseGroups: {},
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      customLabels: {},
      customFields: {},
      caseIds: [],
      newCase: {
        address: {},
      },
      loading: false,
      dobPicker: false,
      rules: {
        required: (value) => !!value || "Required.",
      },
      uniqueIdErrors: [],
      genders: ["Male", "Female"],
    };
  },
  computed: {
    ...mapGetters(["getAccountSettings", "getEntityTypes", "getIdTypes"]),
    idIssueDatePicker() {
      return this.caseIds.map(() => false);
    },
    idExpirationDatePicker() {
      return this.caseIds.map(() => false);
    },
  },
  methods: {
    saveCaseApi,
    saveCasesApi,
    ...mapActions(["setGlobalAlert"]),
    getMinDateString,
    capitalizeFirstLetter,
    addId() {
      this.caseIds.unshift({ ...idStruct });
    },
    deleteId(index) {
      this.caseIds.splice(index, 1);
    },
    resetData() {
      Object.assign(this.$data, this.$options.data.call(this));
    },
    //submit - TODO catch error if case group was deleted prior to saving case
    async onSave() {
      if (!this.$refs.newCaseForm.validate()) {
        return false;
      }
      this.uniqueIdErrors = []

      this.loading = true;

      this.newCase.customLabels = this.customLabels; // It would be good to block empty label arrays here at some point.
      this.newCase.customFields = this.customFields;
      this.newCase.identification = this.caseIds;

      let caseRecordRequest = { ...this.newCase };

      try {
        await this.saveCaseApi(caseRecordRequest).then((caseRecord) => {
          this.$emit("onConfirmed", caseRecord.id);

          // Check if any case reports were opened and display in the alert.
          if (caseRecord.caseReports && caseRecord.caseReports.length > 0) {
            this.setGlobalAlert({
              alert: true,
              color: "red",
              alertTitle: `Case ${this.newCase.name} Added`,
              alertMessage: `${caseRecord.caseReports.length} potential matches reported.`,
            });
          } else {
            this.setGlobalAlert({
              alert: true,
              color: "green",
              alertTitle: `Case ${this.newCase.name} Added`,
            });
          }
          this.resetData();
        });
      } catch (e) {
        if (e.response?.status === 409) {
          this.uniqueIdErrors = ['Local ID already exists']
          this.setGlobalAlert({
            alert: true,
            color: "red",
            alertTitle: `A Case already exists with ID: ${this.newCase.localId}`,
            alertMessage: "Enter a different ID and try again.",
          });
        } else {
          this.setGlobalAlert({
            alert: true,
            color: "red",
            alertMessage: this.$t("error.general.unhandled"),
          });
        }
      } finally {
        this.loading = false;
        this.$refs.newCaseForm.resetValidation();
      }
    },
    async onCsvUploadConfirmed(caseList) {
      this.loading = true;

      try {
        await this.saveCasesApi(caseList).then((newCaseList) => {
          this.$emit("onConfirmed");
          this.setGlobalAlert({
            alert: true,
            color: "green",
            alertTitle: "Success",
            alertMessage: newCaseList.length + " Cases Added",
          });
        });
      } catch (e) {
        if (e.response?.status === 409) {
          this.setGlobalAlert({
            alert: true,
            color: "red",
            alertTitle: "Duplicate IDs found",
            alertMessage: "An ID in your import file already exists in Case Manager. Review your import and remove the duplicate ID."
          });
        } else {
          this.setGlobalAlert({
            alert: true,
            color: "red",
            alertTitle: "Error",
            alertMessage: "Error importing cases, please try again or contact support",
          });
        }
      } finally {
        this.loading = false;
        this.resetData();
      }
    },
    onCancel() {
      this.$emit("onCancelled");
      this.$refs.newCaseForm.resetValidation();
      this.resetData();
    },
  },
};
</script>
